import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LogOut, ShieldCheck, Timer } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

const SESSION_TIMEOUT_MS = 45 * 60 * 1000; // 90 minutes for demo
const WARNING_TIMEOUT_MS = 15 * 60 * 1000;  // 45 minutes warning
const EVENTS = ["mousemove", "keydown", "click", "scroll", "wheel", "touchstart"];

interface SessionTimeoutProps {
  onLogout: () => void;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = ({ onLogout }) => {
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_TIMEOUT_MS / 1000);

  // Fix: Use number | null for browser timers
  const logoutTimer = useRef<number | null>(null);
  const warningTimer = useRef<number | null>(null);
  const countdownInterval = useRef<number | null>(null);

  const isWarningModalOpenRef = useRef(isWarningModalOpen);
  useEffect(() => {
    isWarningModalOpenRef.current = isWarningModalOpen;
  }, [isWarningModalOpen]);

  const handleLogout = useCallback(() => {
    setWarningModalOpen(false);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    if (onLogout) onLogout();
  }, [onLogout]);

  const resetTimers = useCallback(() => {
    if (isWarningModalOpenRef.current) return;

    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);

    setWarningModalOpen(false);
    setCountdown(WARNING_TIMEOUT_MS / 1000);

    warningTimer.current = window.setTimeout(() => {
      setWarningModalOpen(true);
      countdownInterval.current = window.setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (countdownInterval.current) clearInterval(countdownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS - WARNING_TIMEOUT_MS);

    logoutTimer.current = window.setTimeout(handleLogout, SESSION_TIMEOUT_MS);
  }, [handleLogout]);

  const resetButtonTimers = useCallback(() => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);

    setWarningModalOpen(false);
    setCountdown(WARNING_TIMEOUT_MS / 1000);

    warningTimer.current = window.setTimeout(() => {
      setWarningModalOpen(true);
      countdownInterval.current = window.setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            if (countdownInterval.current) clearInterval(countdownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS - WARNING_TIMEOUT_MS);

    logoutTimer.current = window.setTimeout(handleLogout, SESSION_TIMEOUT_MS);
  }, [handleLogout]);

  useEffect(() => {
    const eventHandler = () => resetTimers();
    EVENTS.forEach(event => window.addEventListener(event, eventHandler));
    resetTimers();

    return () => {
      EVENTS.forEach(event => window.removeEventListener(event, eventHandler));
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [resetTimers]);

  useEffect(() => {
    if (countdown === 0) {
      handleLogout();
    }
  }, [countdown, handleLogout]);

  const handleStayLoggedIn = () => {
    resetButtonTimers();
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <Modal
      isOpen={isWarningModalOpen}
      toggle={handleStayLoggedIn}
      centered
      contentClassName="border-0 shadow-lg rounded-4"
      style={{
        backdropFilter: 'blur(2px)',
        width: '400px',
        maxWidth: '90%',
        padding: 0
      }}
    >
      <ModalHeader
        toggle={handleStayLoggedIn}
        className="border-0 rounded-top py-2"
        style={{
          background: 'linear-gradient(90deg, #6366f1 0%, #0ea5e9 100%)',
          color: '#fff',
          fontWeight: 500,
          fontSize: '1.1rem',
          letterSpacing: '0.02em'
        }}
      >
        <span className="d-flex align-items-center">
          <Timer className="me-2" size={20} /> Session Timeout Warning
        </span>
      </ModalHeader>
      <ModalBody className="bg-white text-center rounded-4 py-2" style={{ minHeight: 'auto' }}>
        <div className="d-flex flex-column align-items-center">
          <Timer className="mb-2" size={32} color="#f59e42" style={{ filter: 'drop-shadow(0 0 4px #fde68a)' }} />
          <p className="mt-1 mb-2 text-secondary fs-6" style={{ lineHeight: 1.2 }}>
            You have been inactive. For your security, you will be logged out automatically.
          </p>
          <p className="mt-2 mb-0 fw-bold" style={{ color: '#2563eb', fontSize: '2rem', letterSpacing: '0.05em' }}>
            {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
          </p>
        </div>
      </ModalBody>
      <ModalFooter className="bg-white border-0 rounded-bottom py-2">
        <div className="d-flex flex-row justify-content-center gap-2 w-100">
          <button
            onClick={handleLogout}
            className="d-flex align-items-center justify-content-center btn px-2 py-1"
            style={{
              background: 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)',
              color: '#fff',
              fontWeight: 400,
              fontSize: '1rem',
              border: 'none',
              borderRadius: '0.5rem',
              boxShadow: '0 2px 8px rgba(239,68,68,0.08)'
            }}
          >
            <LogOut size={14} className="me-2" />
            <span>Log Out</span>
          </button>
          <button
            onClick={handleStayLoggedIn}
            className="d-flex align-items-center justify-content-center btn px-2 py-1"
            style={{
              background: 'linear-gradient(90deg, #22d3ee 0%, #2563eb 100%)',
              color: '#fff',
              fontWeight: 400,
              fontSize: '1rem',
              border: 'none',
              borderRadius: '0.5rem',
              boxShadow: '0 2px 8px rgba(37,99,235,0.08)'
            }}
          >
            <ShieldCheck size={14} className="me-2" />
            <span>Stay Logged In</span>
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SessionTimeout;